// A pivot response (POST /organization/reports/pivot) as the table and its
// download see it: pure functions, no React.
//
// The server orders rows and columns for reading (species alphabetically, then
// Unknown species, Bycatch, Empty and Dirty; sites alphabetically, then No
// collection site; time ascending), so the model keeps its order.
import { downloadSheet, sheetFromRows } from "../../../../../utils/spreadsheet";

// The glossary's name for each dimension, for headers and downloads.
export const DIMENSION_LABELS = {
  species: "Species",
  genus: "Genus",
  sex: "Sex",
  collectionSite: "Collection site",
  county: "County",
  siteGroup: "Site group",
  collectionMethod: "Collection method",
  day: "Collection date",
  week: "Week start",
  month: "Month",
  year: "Year",
  weekOfYear: "Week",
};

export const TOTAL_LABEL = "Total count";

// What the catch-all rows and columns mean, for their tooltips.
export const NONE_EXPLANATIONS = {
  "No collection site": "Specimens from trays imaged without a collection site.",
  "No county": "Specimens from collection sites with no county recorded.",
  "No site group": "Specimens from collection sites that are in no site group.",
  "No collection method": "Specimens from trays with no collection method.",
  "Not applicable": "Specimens with no sex: empty and dirty cells, and bycatch.",
};

const key = (r, c) => `${r}:${c}`;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH = /^\d{4}-\d{2}$/;
const asDay = (iso) => new Date(`${iso}T00:00:00Z`);
const dayLabel = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const monthLabel = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

/** A label part as people read it: calendar days and months spelled out. */
export const displayLabel = (part) => {
  if (DAY.test(part)) return dayLabel.format(asDay(part));
  if (MONTH.test(part)) return monthLabel.format(asDay(`${part}-01`));
  return part;
};

// In a spreadsheet, a calendar day is a date cell people can sort and filter.
// SheetJS writes a Date in the browser's local time, so the day is built at
// local midnight: at UTC midnight it lands on the day before anywhere west of
// Greenwich.
const sheetValue = (part) => {
  if (!DAY.test(part)) return part;
  const [y, m, d] = part.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const toModel = (response) => {
  const values = new Map();
  (response?.cells ?? []).forEach(([r, c, count]) => values.set(key(r, c), count));
  return {
    rowDims: response?.meta?.dimensions?.rows ?? [],
    colDims: response?.meta?.dimensions?.columns ?? [],
    rows: response?.rows ?? [],
    columns: response?.columns ?? [],
    values,
    rowTotals: response?.rowTotals ?? [],
    columnTotals: response?.columnTotals ?? [],
    grandTotal: response?.grandTotal ?? 0,
    meta: response?.meta ?? {},
    // Derived columns between the row labels and the counts (Total count and
    // Weekly change, in the weekly report), and their counterparts once
    // transposed: derived rows above the counts. Each {label, values, total}.
    leading: [],
    leadingRows: [],
    // the Total count column on the right and row at the bottom
    totalColumn: true,
    totalRow: true,
  };
};

export const isEmpty = (model) => !model || model.rows.length === 0;

export const cellValue = (model, r, c) => model.values.get(key(r, c)) ?? 0;

/** Rows become columns and columns rows; the counts follow. */
export const transpose = (model) => {
  const values = new Map();
  model.values.forEach((count, k) => {
    const [r, c] = k.split(":");
    values.set(key(c, r), count);
  });
  return {
    ...model,
    rowDims: model.colDims,
    colDims: model.rowDims,
    rows: model.columns,
    columns: model.rows,
    values,
    rowTotals: model.columnTotals,
    columnTotals: model.rowTotals,
    leading: model.leadingRows,
    leadingRows: model.leading,
    totalColumn: model.totalRow,
    totalRow: model.totalColumn,
  };
};

// `display` is a report's own wording for an entry: {text, lines} (see
// speciesByWeekModel.js); otherwise the label parts, dates spelled out.
export const entryLabel = (entry) =>
  entry.display?.text ?? entry.label.map(displayLabel).join(" · ");

/** Header cells for the row dimensions; "Count" when there are none. */
export const rowHeaderLabels = (model) =>
  model.rowDims.map((d) => model.dimensionLabels?.[d] ?? DIMENSION_LABELS[d] ?? d);

/**
 * The table as an array of rows of cells, exactly as shown: row dimension
 * headers, any derived columns, one column per column entry, the Total count
 * column, any derived rows, and the Total count row at the bottom. No ids or
 * internal keys; calendar days are dates.
 */
export const toSheetRows = (model) => {
  const leading = rowHeaderLabels(model);
  const span = Math.max(leading.length, 1);
  const pad = (first) => [first, ...Array(span - 1).fill("")];
  const header = [
    ...(leading.length ? leading : [""]),
    ...model.leading.map((d) => d.label),
    ...model.columns.map(entryLabel),
    ...(model.totalColumn ? [TOTAL_LABEL] : []),
  ];
  const derivedRows = model.leadingRows.map((d) => [
    ...pad(d.label),
    ...model.leading.map(() => ""),
    ...d.values.map((v) => v ?? ""),
    ...(model.totalColumn ? [d.total ?? ""] : []),
  ]);
  const body = model.rows.map((row, r) => [
    ...(leading.length ? (row.display ? [row.display.text] : row.label.map(sheetValue)) : [""]),
    ...model.leading.map((d) => d.values[r] ?? ""),
    ...model.columns.map((_, c) => cellValue(model, r, c)),
    ...(model.totalColumn ? [model.rowTotals[r] ?? 0] : []),
  ]);
  const totals = model.totalRow
    ? [
        [
          ...pad(TOTAL_LABEL),
          ...model.leading.map((d) => d.total ?? ""),
          ...model.columnTotals,
          ...(model.totalColumn ? [model.grandTotal] : []),
        ],
      ]
    : [];
  return [header, ...derivedRows, ...body, ...totals];
};

export const exportPivotXlsx = (model, title) =>
  downloadSheet(sheetFromRows(toSheetRows(model), { cellDates: true, dateNF: "yyyy-mm-dd" }), "Report", title);
